import { useMemo, useState } from "react";
import { format, parseISO, subDays } from "date-fns";
import type { DateRange } from "react-day-picker";
import { CalendarDays, Filter, Info, ListChecks, RefreshCw, Settings2 } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Calendar } from "@/modern/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/modern/components/ui/hover-card";
import { Toggle } from "@/modern/components/ui/toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { CLOCKING_GUIDE_TARGETS, PageGuideTrigger, guideTargetProps } from "@/react/guides";
import type { ClockingLegendItem } from "./types";
import { IncentivesEnabledChip, StatusLegend } from "./StatusLegend";

export type ClockingToolbarProps = {
  startDate: string;
  endDate: string;
  exceptionsOnly: boolean;
  canManage: boolean;
  isRefreshing?: boolean;
  incentivesEnabled?: boolean;
  clockInPoints?: number;
  clockOutPoints?: number;
  missedClockPenalty?: number;
  legend: ClockingLegendItem[];
  exceptionCount?: number;
  inboxOpen?: boolean;
  onRangeChange: (startDate: string, endDate: string) => void;
  onExceptionsOnlyChange: (value: boolean) => void;
  onRefresh: () => void;
  onOpenInbox?: () => void;
  onOpenSettings?: () => void;
  onShowGuide?: () => void;
};

function toIsoDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

function fromIso(iso: string): Date {
  return parseISO(iso);
}

export function defaultClockingDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = subDays(end, 6);
  return {
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
  };
}

function formatRangeLabel(startDate: string, endDate: string): string {
  try {
    const start = fromIso(startDate);
    const end = fromIso(endDate);
    if (format(start, "MMM yyyy") === format(end, "MMM yyyy")) {
      return `${format(start, "MMM d")} – ${format(end, "d, yyyy")}`;
    }
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  } catch {
    return `${startDate} – ${endDate}`;
  }
}

export function ClockingToolbar({
  startDate,
  endDate,
  exceptionsOnly,
  canManage,
  isRefreshing,
  incentivesEnabled,
  clockInPoints,
  clockOutPoints,
  missedClockPenalty,
  legend,
  exceptionCount = 0,
  inboxOpen,
  onRangeChange,
  onExceptionsOnlyChange,
  onRefresh,
  onOpenInbox,
  onOpenSettings,
  onShowGuide,
}: ClockingToolbarProps) {
  const [rangeOpen, setRangeOpen] = useState(false);
  // Draft while the popover is open so a complete range can restart from a new start.
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();

  const committedRange = useMemo<DateRange>(
    () => ({ from: fromIso(startDate), to: fromIso(endDate) }),
    [startDate, endDate]
  );
  const selectedRange = draftRange ?? committedRange;

  const handleRangeOpenChange = (open: boolean) => {
    setRangeOpen(open);
    if (open) {
      setDraftRange({ from: committedRange.from, to: committedRange.to });
    } else {
      setDraftRange(undefined);
    }
  };

  const handleRangeSelect = (range: DateRange | undefined, triggerDate: Date) => {
    const current = draftRange ?? committedRange;

    // Complete range → any click starts a new start/end selection.
    if (current.from && current.to) {
      setDraftRange({ from: triggerDate, to: undefined });
      return;
    }

    if (!range?.from) {
      setDraftRange(undefined);
      return;
    }

    if (!range.to) {
      setDraftRange({ from: range.from, to: undefined });
      return;
    }

    onRangeChange(toIsoDate(range.from), toIsoDate(range.to));
    handleRangeOpenChange(false);
  };

  return (
    <div className="flex h-9 items-center gap-1.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" {...guideTargetProps(CLOCKING_GUIDE_TARGETS.dateRange)}>
            <Popover open={rangeOpen} onOpenChange={handleRangeOpenChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 font-normal tabular-nums"
                  aria-label="Date range"
                >
                  <CalendarDays className="size-3.5 text-muted-foreground" />
                  <span className="text-xs">{formatRangeLabel(startDate, endDate)}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0" sideOffset={6}>
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  defaultMonth={selectedRange.from}
                  selected={selectedRange}
                  onSelect={handleRangeSelect}
                />
              </PopoverContent>
            </Popover>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[240px] text-left">
          Choose the date range for attendance review.
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" {...guideTargetProps(CLOCKING_GUIDE_TARGETS.exceptionsOnly)}>
            <Toggle
              size="sm"
              variant="outline"
              pressed={exceptionsOnly}
              onPressedChange={onExceptionsOnlyChange}
              aria-label="Exceptions only"
              className={cn(
                "h-8 gap-1.5 px-2.5 text-xs data-[state=on]:border-primary/40 data-[state=on]:bg-primary/10 data-[state=on]:text-foreground"
              )}
            >
              <Filter className="size-3.5" />
              Exceptions only
            </Toggle>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[240px] text-left">
          Show only problem punches and time off. Turn off to include complete days.
        </TooltipContent>
      </Tooltip>

      <HoverCard openDelay={120} closeDelay={100}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            className="inline-flex h-8 cursor-default items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            aria-label="Status legend"
            {...guideTargetProps(CLOCKING_GUIDE_TARGETS.legend)}
          >
            <Info className="size-3.5" />
            Legend
          </button>
        </HoverCardTrigger>
        <HoverCardContent align="start" side="bottom" className="w-64 p-3" sideOffset={6}>
          <p className="mb-2 text-xs font-medium text-foreground">Status colors</p>
          {legend.length > 0 ? (
            <StatusLegend items={legend} dense />
          ) : (
            <p className="text-xs text-muted-foreground">Colors appear when attendance loads.</p>
          )}
        </HoverCardContent>
      </HoverCard>

      <div className="ml-auto flex items-center gap-1.5">
        {onOpenInbox ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={inboxOpen ? "secondary" : exceptionCount > 0 ? "default" : "outline"}
                size="sm"
                className="h-8 gap-1.5 px-2.5 text-xs tabular-nums"
                onClick={onOpenInbox}
                aria-label={
                  exceptionCount > 0
                    ? `Open clear queue, ${exceptionCount} open across all days`
                    : "Open clear queue for all days"
                }
                {...guideTargetProps(CLOCKING_GUIDE_TARGETS.inbox)}
              >
                <ListChecks className="size-3.5" />
                {exceptionCount > 0 ? `Clear queue · ${exceptionCount}` : "Clear queue"}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px] text-left">
              Clear open exceptions across every day in this range. Click a date header (Clear) for one day.
            </TooltipContent>
          </Tooltip>
        ) : null}

        {incentivesEnabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <IncentivesEnabledChip
                  clockInPoints={clockInPoints}
                  clockOutPoints={clockOutPoints}
                  missedClockPenalty={missedClockPenalty}
                  onClick={canManage ? onOpenSettings : undefined}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[260px] text-left">
              {canManage
                ? "Incentives are on for this board. Click to open settings."
                : "Incentives are on for this board. Clean punches earn points; missed clocks may add a penalty."}
            </TooltipContent>
          </Tooltip>
        ) : null}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="size-8 shrink-0 p-0"
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label="Refresh"
            >
              <RefreshCw className={cn("size-3.5", isRefreshing && "animate-spin")} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Refresh attendance</TooltipContent>
        </Tooltip>

        {canManage && onOpenSettings ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="size-8 shrink-0 p-0"
                onClick={onOpenSettings}
                aria-label="Settings"
                {...guideTargetProps(CLOCKING_GUIDE_TARGETS.settings)}
              >
                <Settings2 className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Clocking settings</TooltipContent>
          </Tooltip>
        ) : null}

        {onShowGuide ? (
          <PageGuideTrigger
            onClick={onShowGuide}
            className="size-8 shrink-0 rounded-md border border-input bg-background text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
          />
        ) : null}
      </div>
    </div>
  );
}
